import styled from "@emotion/styled";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CheckboxFilter,
  formatBalance,
  getFirstWord,
  greenTag,
  imageFromPlatformName,
  redTag,
  StepTable,
  TagInfo,
  TokenTag,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useLanguageContext } from "../../../contexts/language/language";
import type { MarginOpenOrder } from "../../../types/margin";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { ExpiryPopover, IconTextContainer, PAGE_SIZE_OPTIONS, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

interface MarginOpenOrdersProps {
  orders: MarginOpenOrder[];
}
export const MarginOpenOrders: FC<MarginOpenOrdersProps> = ({ orders }) => {
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "marginOpenOrdersPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      components: {
        table: { headers },
      },
      pages: {
        dashboard: {
          modules: {
            marginTrading: { marginOpenOrders: marginOpenOrdersText },
          },
        },
      },
    },
  } = useLanguageContext();

  const totalOpenOrdersValue = useMemo(() => {
    return orders.reduce((total, currentValue) => {
      return total + currentValue.size;
    }, 0);
  }, [orders]);

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(orders.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [orders.length, paginationSetting]);

  const openOrderColumns: StepColumnsType<MarginOpenOrder> = [
    {
      title: common.asset,
      key: "kind",
      dataIndex: "kind",
      render(_, record) {
        return (
          <AssetContainer>
            <TokenContainer>
              <URLIcon src={record.logoURI} />
              <TokenIconText bold>{record.market}</TokenIconText>
              {record.expiry && <ExpiryPopover expiryTimeStamp={record.expiry} />}
            </TokenContainer>

            <TokenTag
              icon={<URLIcon size={16} src={imageFromPlatformName(record.kind) ?? ""} />}
              text={getFirstWord(record.kind) ?? ""}
              uppercase={false}
            />
          </AssetContainer>
        );
      },
    },
    {
      title: common.side,
      key: "side",
      dataIndex: "side",
      className: "column-center",
      width: 110,
      filters: [
        {
          text: "Buy",
          value: "buy",
        },
        {
          text: "Sell",
          value: "sell",
        },
      ],
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.side.startsWith(value as "buy" | "sell"),
      filteredValue: filteredInfo.side ?? null,
      render(side: string) {
        const tagColor = side === "buy" ? greenTag : redTag;
        return (
          <div>
            <TagInfo useSpaceMono={false} tagType="text" tagColorOverride={tagColor}>
              {side === "buy" ? common.buy : common.sell}
            </TagInfo>
          </div>
        );
      },
    },
    {
      title: common.amount,
      key: "amount",
      dataIndex: "amount",
      className: "column-number",
      sorter: (a, b) => a.amount - b.amount,
      render(amount: number, { baseDecimals }) {
        return <CellValue cellType="number">{formatBalance(amount, baseDecimals)}</CellValue>;
      },
    },
    {
      title: headers.price,
      key: "price",
      dataIndex: "price",
      className: "column-number",
      sorter: (a, b) => a.price - b.price,
      render(price: number) {
        return <CellValue cellType="value">{price}</CellValue>;
      },
    },
    {
      title: common.value,
      key: "size",
      dataIndex: "size",
      className: "column-number",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.size - b.size,
      render(size: number) {
        return (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {size}
          </CellValue>
        );
      },
    },
  ];

  return (
    <OpenOrdersContainer>
      <ChevronCollapse title={marginOpenOrdersText.title} value={totalOpenOrdersValue} isCollapseChild>
        <StepTable
          pagination={pagination}
          onChange={(pagination, filters) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            setFilteredInfo(filters);
          }}
          columns={openOrderColumns}
          rowKey={(record) => `${record.market}-${record.kind}-${record.side}`}
          dataSource={orders}
        />
      </ChevronCollapse>
    </OpenOrdersContainer>
  );
};

const AssetContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TokenContainer = styled(IconTextContainer)`
  margin-right: 16px;
`;

const OpenOrdersContainer = styled.div`
  margin-top: 20px;
`;
